import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from './api.js';
import { useSession } from './session.js';

/**
 * Saved order for a reorderable widget grid (`components/DashboardGrid.tsx`),
 * one instance per role dashboard (`storageKey` is e.g. `'dashboard:ADMIN'`).
 *
 * Same admin exception as `lib/theme.tsx`, for the same reason:
 * `users_self_update` cannot reach the admin's row at all, so `PATCH /api/me`
 * refuses a `dashboardLayout` change for that one actor. Rather than the
 * drag handle silently failing for the admin, this keeps their order in
 * localStorage instead — browser-local rather than following the account.
 * Everyone else is DB-backed via `/api/me`, already loaded by
 * `SessionProvider`, so there is nothing extra to fetch here.
 */

function localStorageKey(storageKey: string): string {
  return `perlaprop-dashboard-layout-${storageKey}`;
}

function readLocalOrder(storageKey: string): string[] | null {
  try {
    const raw = window.localStorage.getItem(localStorageKey(storageKey));
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.every((id) => typeof id === 'string') ? parsed : null;
  } catch {
    return null;
  }
}

/** Saved ids that are still known, in saved order, then any known ids the
 *  save never saw — a widget added after the order was saved, or a stale
 *  order left over from a role change — appended in their default order. */
function mergeOrder(knownIds: string[], saved: string[] | null): string[] {
  if (!saved) return knownIds;
  const known = new Set(knownIds);
  const savedKnown = saved.filter((id) => known.has(id));
  const savedSet = new Set(savedKnown);
  const missing = knownIds.filter((id) => !savedSet.has(id));
  return [...savedKnown, ...missing];
}

export function useDashboardOrder(
  storageKey: string,
  knownIds: string[],
): {
  orderedIds: string[];
  setOrder: (ids: string[]) => void;
  /** Set when a non-admin staff member's reorder PATCH fails; cleared on retry. */
  error: unknown;
} {
  const { state, refresh } = useSession();
  const actor = state.status === 'signed-in' ? state.me.actor : null;
  const isAdmin = actor?.staffRole === 'ADMIN';

  // Read speculatively, same as theme.tsx's readLocalTheme(): if this turns
  // out to be a non-admin, the effect below overwrites it the moment the
  // session resolves. Reading unconditionally (rather than gating on
  // `isAdmin`, which isn't known yet on the first render) is what avoids a
  // flash of the default order for the admin on every load.
  const [savedOrder, setSavedOrder] = useState<string[] | null>(() => readLocalOrder(storageKey));
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (actor && !isAdmin) setSavedOrder(actor.dashboardLayout);
  }, [actor, isAdmin]);

  const orderedIds = useMemo(() => mergeOrder(knownIds, savedOrder), [knownIds, savedOrder]);

  const setOrder = useCallback(
    (ids: string[]) => {
      const previous = savedOrder;
      setError(null);
      setSavedOrder(ids);
      if (isAdmin) {
        window.localStorage.setItem(localStorageKey(storageKey), JSON.stringify(ids));
        return;
      }
      // Optimistic, same shape as theme.tsx's setTheme: the grid already
      // shows the new order immediately. A failure reverts local state and
      // records the error — nothing awaits this call directly.
      void api('/me', { method: 'PATCH', body: JSON.stringify({ dashboardLayout: ids }) })
        .then(() => refresh())
        .catch((caught: unknown) => {
          setSavedOrder(previous);
          setError(caught);
        });
    },
    [isAdmin, refresh, savedOrder, storageKey],
  );

  return { orderedIds, setOrder, error };
}
